import React from 'react';
import { format, formatDistanceToNow } from 'date-fns';

// ── Date helpers ──────────────────────────────────────────────────────────────
export const fmtDate   = d => d ? format(new Date(d), 'dd MMM yyyy') : '—';
export const fmtDT     = d => d ? format(new Date(d), 'dd MMM yyyy, HH:mm') : '—';
export const fmtAgo    = d => d ? formatDistanceToNow(new Date(d), { addSuffix: true }) : '—';
export const fmtTime   = d => d ? format(new Date(d), 'HH:mm') : '—';

// ── Currency ──────────────────────────────────────────────────────────────────
export const fmtPKR = (v) =>
  new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR', maximumFractionDigits: 0 }).format(v || 0);

export const fmtNum = (v) =>
  new Intl.NumberFormat('en').format(v || 0);

// ── Shipment status ───────────────────────────────────────────────────────────
export const STATUS_BADGE = {
  BOOKING_CONFIRMED:  { cls: 'badge-info',    label: 'Confirmed' },
  PICKUP_SCHEDULED:   { cls: 'badge-info',    label: 'Pickup Scheduled' },
  PICKED_UP:          { cls: 'badge-navy',    label: 'Picked Up' },
  IN_TRANSIT:         { cls: 'badge-navy',    label: 'In Transit' },
  AT_HUB:             { cls: 'badge-purple',  label: 'At Hub' },
  OUT_FOR_DELIVERY:   { cls: 'badge-warning', label: 'Out for Delivery' },
  DELIVERED:          { cls: 'badge-success', label: 'Delivered' },
  DELIVERY_ATTEMPTED: { cls: 'badge-warning', label: 'Attempted' },
  RTO_INITIATED:      { cls: 'badge-danger',  label: 'RTO Initiated' },
  RTO_IN_TRANSIT:     { cls: 'badge-danger',  label: 'RTO In Transit' },
  RTO_DELIVERED:      { cls: 'badge-gray',    label: 'RTO Delivered' },
  CANCELLED:          { cls: 'badge-gray',    label: 'Cancelled' },
  LOST:               { cls: 'badge-danger',  label: 'Lost' },
  DAMAGED:            { cls: 'badge-danger',  label: 'Damaged' },
};

export const StatusBadge = ({ status }) => {
  const s = STATUS_BADGE[status] || { cls: 'badge-gray', label: status };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
};

// ── Ticket status / priority ──────────────────────────────────────────────────
export const TICKET_STATUS_BADGE = {
  OPEN:             { cls: 'badge-danger',  label: 'Open' },
  IN_PROGRESS:      { cls: 'badge-warning', label: 'In Progress' },
  PENDING_CUSTOMER: { cls: 'badge-info',    label: 'Pending Customer' },
  PENDING_THIRD_PARTY: { cls: 'badge-info', label: 'Pending 3rd Party' },
  RESOLVED:         { cls: 'badge-success', label: 'Resolved' },
  CLOSED:           { cls: 'badge-gray',    label: 'Closed' },
};

export const PRIORITY_BADGE = {
  LOW:    { cls: 'badge-gray',    label: 'Low' },
  MEDIUM: { cls: 'badge-info',    label: 'Medium' },
  HIGH:   { cls: 'badge-warning', label: 'High' },
  URGENT: { cls: 'badge-danger',  label: 'Urgent' },
};

export const TicketStatusBadge = ({ status }) => {
  const s = TICKET_STATUS_BADGE[status] || { cls: 'badge-gray', label: status };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
};

export const PriorityBadge = ({ priority }) => {
  const p = PRIORITY_BADGE[priority] || { cls: 'badge-gray', label: priority };
  return <span className={`badge ${p.cls}`}>{p.label}</span>;
};

// ── Service type labels ───────────────────────────────────────────────────────
export const SERVICE_LABELS = {
  DOM_STD:        'Domestic Standard',
  DOM_EXP:        'Domestic Express',
  DOM_SD:         'Same Day',
  DOM_ECO:        'Economy',
  INT_AIR:        'International Air',
  INT_SEA:        'International Sea',
  INT_EXP:        'International Express',
  CARGO_BULK:     'Cargo Bulk',
  CARGO_CONTAINER:'Cargo Container',
  CARGO_TEMP:     'Temperature Controlled',
};

// ── Payment status ────────────────────────────────────────────────────────────
export const PAYMENT_BADGE = {
  PENDING:   { cls: 'badge-warning', label: 'Pending' },
  PAID:      { cls: 'badge-success', label: 'Paid' },
  OVERDUE:   { cls: 'badge-danger',  label: 'Overdue' },
  CANCELLED: { cls: 'badge-gray',    label: 'Cancelled' },
  REFUNDED:  { cls: 'badge-info',    label: 'Refunded' },
};

export const PaymentBadge = ({ status }) => {
  const s = PAYMENT_BADGE[status] || { cls: 'badge-gray', label: status };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
};

// ── Role labels ───────────────────────────────────────────────────────────────
export const ROLE_LABELS = {
  ADMIN:'System Admin', MANAGEMENT:'Management', STAFF:'Staff',
  RIDER:'Rider', SALES:'Sales Rep', FINANCE:'Finance', HR:'HR',
  CS:'Customer Service', FRANCHISE:'Franchise', B2C:'Customer', B2B:'Corporate',
};

// ── Misc ──────────────────────────────────────────────────────────────────────
export const truncate = (s, n = 40) => s && s.length > n ? s.slice(0, n) + '…' : s;
export const addrStr = (a) => {
  if (!a) return '—';
  if (typeof a === 'string') { try { a = JSON.parse(a); } catch { return a; } }
  return [a.line1, a.city, a.state].filter(Boolean).join(', ');
};
